import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth, useAuthPrompt } from '../App';
import { 
  getTransactions, 
  scanReceipt, 
  uploadBankStatement, 
  getPendingBankImports,
  approveBankImports,
  createManualEntry,
  getExpenseCategories,
  getPlaidLinkToken,
  exchangePlaidToken,
  getPlaidStatus,
  syncPlaidTransactions
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Receipt,
  Upload,
  Plus,
  List,
  Camera,
  FileText,
  Loader2,
  CheckCircle,
  XCircle,
  Calendar,
  DollarSign,
  Tag,
  Search,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Trash2,
  RefreshCw,
  AlertCircle,
  Building2,
  Link2,
  Unlink,
  CreditCard,
  Landmark,
  Wallet,
  ArrowRight
} from 'lucide-react';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(Math.abs(amount || 0));
}

export default function ExpensesManage({ context }) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const initialTab = searchParams.get('tab') || 'link';
  const dataContext = context || (location.pathname.startsWith('/personal') ? 'personal' : 'business');
  
  const auth = useAuth();
  const authPrompt = useAuthPrompt();
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [categories, setCategories] = useState({ expense_categories: [], income_sources: [] });

  useEffect(function loadCategories() {
    getExpenseCategories().then(function(res) {
      setCategories(res.data);
    }).catch(function(err) {
      console.error('Failed to load categories:', err);
    });
  }, []);

  function requireAuth(action) {
    if (!auth.isAuthenticated) {
      authPrompt.showAuthPrompt(action);
      return false;
    }
    return true;
  }

  const tabClass = 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all';
  const activeClass = tabClass + ' bg-[hsl(var(--accent))] text-black';
  const inactiveClass = tabClass + ' bg-muted text-muted-foreground hover:text-foreground';

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="expenses-manage">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
          {dataContext === 'personal' ? 'Personal' : 'Business'} Expenses
        </h1>
        <p className="text-muted-foreground mt-1">Link your bank account or add expenses manually</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        <button onClick={function() { setActiveTab('link'); }} className={activeTab === 'link' ? activeClass : inactiveClass} data-testid="tab-link">
          <Building2 className="w-4 h-4" /> <span className="hidden sm:inline">Link Bank</span>
        </button>
        <button onClick={function() { setActiveTab('scan'); }} className={activeTab === 'scan' ? activeClass : inactiveClass} data-testid="tab-scan">
          <Camera className="w-4 h-4" /> <span className="hidden sm:inline">Scan Receipt</span>
        </button>
        <button onClick={function() { setActiveTab('bank'); }} className={activeTab === 'bank' ? activeClass : inactiveClass} data-testid="tab-bank">
          <Upload className="w-4 h-4" /> <span className="hidden sm:inline">Import Statement</span>
        </button>
        <button onClick={function() { setActiveTab('manual'); }} className={activeTab === 'manual' ? activeClass : inactiveClass} data-testid="tab-manual">
          <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Manual Entry</span>
        </button>
        <button onClick={function() { setActiveTab('ledger'); }} className={activeTab === 'ledger' ? activeClass : inactiveClass} data-testid="tab-ledger">
          <List className="w-4 h-4" /> <span className="hidden sm:inline">Ledger</span>
        </button>
      </div>

      {activeTab === 'link' && <BankLinkTab requireAuth={requireAuth} dataContext={dataContext} />}
      {activeTab === 'scan' && <ScanReceiptTab requireAuth={requireAuth} categories={categories.expense_categories} />}
      {activeTab === 'bank' && <BankStatementTab requireAuth={requireAuth} />}
      {activeTab === 'manual' && <ManualEntryTab requireAuth={requireAuth} categories={categories.expense_categories} />}
      {activeTab === 'ledger' && <LedgerTab dataContext={dataContext} />}
    </div>
  );
}

function BankLinkTab({ requireAuth, dataContext }) {
  const [plaidStatus, setPlaidStatus] = useState({ connected: false, items_count: 0, accounts: [] });
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(function fetchStatus() {
    getPlaidStatus().then(function(res) {
      setPlaidStatus(res.data);
    }).catch(function(err) {
      console.error('Failed to fetch Plaid status:', err);
    }).finally(function() {
      setLoading(false);
    });
  }, []);

  function handleLinkBank() {
    if (!requireAuth('Link Bank Account')) return;
    setLinking(true);
    
    getPlaidLinkToken().then(function(tokenRes) {
      const linkToken = tokenRes.data.link_token;
      if (!linkToken) {
        toast.error('Failed to initialize bank linking. Please ensure Plaid API keys are configured.');
        setLinking(false);
        return;
      }
      
      if (window.Plaid) {
        const handler = window.Plaid.create({
          token: linkToken,
          onSuccess: function(publicToken, metadata) {
            exchangePlaidToken(publicToken, metadata.institution?.name).then(function() {
              toast.success('Bank account linked successfully!');
              getPlaidStatus().then(function(res) { setPlaidStatus(res.data); });
            }).catch(function() {
              toast.error('Failed to link bank account');
            });
          },
          onExit: function(err) {
            if (err) toast.error('Bank linking cancelled');
            setLinking(false);
          }
        });
        handler.open();
      } else {
        toast.error('Plaid SDK not loaded.');
        setLinking(false);
      }
    }).catch(function() {
      toast.error('Failed to start bank linking.');
      setLinking(false);
    });
  }

  function handleSync() {
    if (!requireAuth('Sync Transactions')) return;
    setSyncing(true);
    syncPlaidTransactions().then(function(res) {
      toast.success('Synced ' + (res.data.transactions_added || 0) + ' transactions!');
    }).catch(function() {
      toast.error('Failed to sync transactions');
    }).finally(function() {
      setSyncing(false);
    });
  }

  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-2">Loading bank connection status...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="card-premium overflow-hidden">
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-[hsl(var(--accent))]" />
            Bank Account Connection
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your bank to automatically import {dataContext} expenses
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {plaidStatus.connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-emerald-500/20">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-400">Bank Connected</p>
                    <p className="text-sm text-muted-foreground">{plaidStatus.items_count} account(s) linked</p>
                  </div>
                </div>
                <Button variant="outline" onClick={handleSync} disabled={syncing} className="border-emerald-500/30 hover:bg-emerald-500/10">
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sync Now
                </Button>
              </div>
              <Button variant="outline" onClick={handleLinkBank} disabled={linking} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Link Another Account
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Bank Account Connected</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Link your bank account to automatically import transactions and categorize expenses.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-muted/30 border border-border text-center">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--accent))]" />
                  <p className="font-medium text-sm">Auto-Sync</p>
                  <p className="text-xs text-muted-foreground">Import transactions daily</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 border border-border text-center">
                  <Tag className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--accent))]" />
                  <p className="font-medium text-sm">Smart Categorization</p>
                  <p className="text-xs text-muted-foreground">AI-powered expense sorting</p>
                </div>
                <div className="p-4 rounded-lg bg-muted/30 border border-border text-center">
                  <Wallet className="w-6 h-6 mx-auto mb-2 text-[hsl(var(--accent))]" />
                  <p className="font-medium text-sm">Tax Ready</p>
                  <p className="text-xs text-muted-foreground">Organized for tax time</p>
                </div>
              </div>
              <Button onClick={handleLinkBank} disabled={linking} className="w-full h-12 text-base font-semibold bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black" data-testid="link-bank-button">
                {linking ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Connecting...</> : <><Link2 className="w-5 h-5 mr-2" /> Link Bank Account</>}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-emerald-500" />
                  Bank-level encryption • Read-only access • Plaid secured
                </span>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {!plaidStatus.connected && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-500">Plaid API Key Required</p>
                <p className="text-sm text-muted-foreground mt-1">
                  To enable automatic bank linking, provide your Plaid API credentials.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ScanReceiptTab({ requireAuth, categories }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  function handleFileSelect(e) {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      const reader = new FileReader();
      reader.onload = function(ev) { setPreview(ev.target && ev.target.result); };
      reader.readAsDataURL(selectedFile);
    }
  }

  function handleScan() {
    if (!requireAuth('Scan Receipt')) return;
    if (!file) { toast.error('Please select a file first'); return; }
    
    setScanning(true);
    const formData = new FormData();
    formData.append('file', file);
    
    scanReceipt(formData).then(function(res) {
      setResult(res.data);
      toast.success('Receipt scanned and saved! Check Receipts tab for the stored receipt.');
    }).catch(function(err) {
      toast.error('Failed to scan receipt');
    }).finally(function() {
      setScanning(false);
    });
  }

  function clearFile() {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-[hsl(var(--accent))]" />
          Scan Receipt
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          AI will extract vendor, amount, date and auto-categorize. Receipt is saved to the Receipts tab.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-[hsl(var(--accent))]/50 transition-colors cursor-pointer" onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}>
          <input ref={fileInputRef} type="file" accept="image/*,.pdf" onChange={handleFileSelect} className="hidden" data-testid="receipt-file-input" />
          {preview ? (
            <div className="space-y-4">
              <img src={preview} alt="Receipt preview" className="max-h-48 mx-auto rounded-lg" />
              <p className="text-sm text-muted-foreground">{file && file.name}</p>
              <Button variant="ghost" size="sm" onClick={function(e) { e.stopPropagation(); clearFile(); }}>
                <XCircle className="w-4 h-4 mr-2" /> Clear
              </Button>
            </div>
          ) : (
            <div>
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">Click to upload receipt</p>
              <p className="text-sm text-muted-foreground">PNG, JPG, or PDF</p>
            </div>
          )}
        </div>

        <Button onClick={handleScan} disabled={scanning || !file} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black">
          {scanning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning with AI...</> : <><Camera className="w-4 h-4 mr-2" /> Scan & Save Receipt</>}
        </Button>

        {result && (
          <Card className="bg-muted/30 border-[hsl(var(--accent))]/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Receipt Processed & Saved
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Vendor:</span>
                <span className="font-medium">{result.vendor || 'Unknown'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Amount:</span>
                <span className="font-semibold text-red-400">{formatCurrency(result.amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Date:</span>
                <span className="font-medium">{result.date || 'Unknown'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Category:</span>
                <Badge variant="outline" className="bg-[hsl(var(--accent))]/10">{result.category || 'Uncategorized'}</Badge>
              </div>
              {result.description && (
                <div className="pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">{result.description}</span>
                </div>
              )}
              <div className="pt-2 border-t border-border flex items-center gap-2 text-sm text-emerald-500">
                <Receipt className="w-4 h-4" />
                <span>Saved to Receipts tab & Expense created</span>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}

function BankStatementTab({ requireAuth }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  function handleFileSelect(e) {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  }

  function handleUpload() {
    if (!requireAuth('Upload Bank Statement')) return;
    if (!file) { toast.error('Please select a file first'); return; }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    uploadBankStatement(formData).then(function(res) {
      toast.success('Imported ' + (res.data.imported_count || 0) + ' transactions!');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }).catch(function() {
      toast.error('Failed to upload statement');
    }).finally(function() {
      setUploading(false);
    });
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-[hsl(var(--accent))]" />
          Import Bank Statement
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-[hsl(var(--accent))]/50 transition-colors cursor-pointer" onClick={function() { fileInputRef.current && fileInputRef.current.click(); }}>
          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.pdf" onChange={handleFileSelect} className="hidden" data-testid="statement-file-input" />
          {file ? (
            <div>
              <FileText className="w-12 h-12 mx-auto text-[hsl(var(--accent))] mb-4" />
              <p className="font-medium">{file.name}</p>
              <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          ) : (
            <div>
              <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">Click to upload statement</p>
              <p className="text-sm text-muted-foreground">CSV, XLSX, or PDF</p>
            </div>
          )}
        </div>

        <Button onClick={handleUpload} disabled={uploading || !file} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black">
          {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</> : <><Upload className="w-4 h-4 mr-2" /> Import Transactions</>}
        </Button>
      </CardContent>
    </Card>
  );
}

function ManualEntryTab({ requireAuth, categories }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    if (!requireAuth('Add Expense')) return;
    if (!amount || !description) { toast.error('Please fill in required fields'); return; }
    
    setSaving(true);
    createManualEntry({
      amount: -Math.abs(parseFloat(amount)),
      description: description,
      category: category || 'Uncategorized',
      date: date,
      type: 'expense'
    }).then(function() {
      toast.success('Expense added!');
      setAmount(''); setDescription(''); setCategory('');
    }).catch(function() {
      toast.error('Failed to add expense');
    }).finally(function() {
      setSaving(false);
    });
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="w-5 h-5 text-[hsl(var(--accent))]" />
          Manual Entry
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <Input id="amount" type="number" step="0.01" placeholder="0.00" value={amount} onChange={function(e) { setAmount(e.target.value); }} required data-testid="expense-amount" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input id="date" type="date" value={date} onChange={function(e) { setDate(e.target.value); }} data-testid="expense-date" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Input id="description" placeholder="What was this expense for?" value={description} onChange={function(e) { setDescription(e.target.value); }} required data-testid="expense-description" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input id="category" placeholder="e.g., Office Supplies, Travel" value={category} onChange={function(e) { setCategory(e.target.value); }} data-testid="expense-category" />
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-black">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Add Expense</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function LedgerTab({ dataContext }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(function loadTransactions() {
    getTransactions({ limit: 50 }).then(function(res) {
      const expenses = (res.data.transactions || []).filter(function(t) { return t.amount < 0; });
      setTransactions(expenses);
    }).catch(function() {
      toast.error('Failed to load transactions');
    }).finally(function() {
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-2">Loading expenses...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <List className="w-5 h-5 text-[hsl(var(--accent))]" />
          Expense Ledger
        </CardTitle>
      </CardHeader>
      <CardContent>
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No expenses recorded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.slice(0, 20).map(function(txn, i) {
              return (
                <div key={txn.id || i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-3">
                    <ArrowDownRight className="w-4 h-4 text-red-400" />
                    <div>
                      <p className="font-medium">{txn.description || txn.vendor || 'Expense'}</p>
                      <p className="text-xs text-muted-foreground">{txn.date} • {txn.category || 'Uncategorized'}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-red-400">{formatCurrency(txn.amount)}</p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
